import Select from '@/components/Select';
import { Can } from '@/components/can/Can';
import { Tabs } from '@/components/tabs/Tabs';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { ReferralOverviewCards } from '@/features/dashboard/referralReports/components/ReferralOverviewCards';
import { useAffiliateReferralReport } from '@/features/dashboard/referralReports/hooks/useAffiliateReferralReport';
import { useReferralAnalytics } from '@/features/dashboard/referralReports/hooks/useReferralAnalytics';
import { AffiliateReferralReportTable } from '@/features/dashboard/referralReports/table/affiliateReferralReport';
import { CustomerReferralStage1Table } from '@/features/dashboard/referralReports/table/customerReferralStage1';
import { CustomerReferralStage2Table } from '@/features/dashboard/referralReports/table/customerReferralStage2';
import { useDynamicLocale } from '@/hoslog-intl';
import { ReferralReportTab, ReferralUserType } from '@/services/dashboard/referralReports/referralReports.types';
import { createFileRoute, SearchSchemaInput, useSearch } from '@tanstack/react-router';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { z } from 'zod';

const sortItemSchema = z.object({
  id: z.enum([
    'referrerName',
    'reservedBy',
    'reservationDateTime',
    'pointsEarned',
    'stageDetails',
    'createdDate',
    'affiliateInfo',
    'referredTo',
    'userType',
    'rewardPoints',
    'updatedDate',
  ]),
  desc: z.boolean(),
});

const referralReportsSearchSchema = z.object({
  activeTab: z.nativeEnum(ReferralReportTab).optional(),
  userType: z.nativeEnum(ReferralUserType).optional(),
  q: z
    .any()
    .transform((val) => (val == null ? undefined : String(val)))
    .optional(),
  page: z.coerce.number().nonnegative().catch(0),
  fetch: z.coerce.number().positive().max(100).catch(10),
  createdDate: z.union([z.string(), z.array(z.string())]).optional(),
  sort: z
    .preprocess((value) => {
      if (typeof value === 'string') {
        try {
          return JSON.parse(value);
        } catch {
          return undefined;
        }
      }
      return value;
    }, z.array(sortItemSchema))
    .optional(),
});

export const Route = createFileRoute('/(app)/dashboard/referral-reports/')({
  component: RouteComponent,
  validateSearch: (search: SearchSchemaInput) => referralReportsSearchSchema.parse(search),
});

function RouteComponent() {
  const { t } = useDynamicLocale();
  const { activeTab, userType = ReferralUserType.Customer } = useSearch({
    from: '/(app)/dashboard/referral-reports/',
  });
  const [activeTabProp, setActiveTabProp] = useQueryState(
    'activeTab',
    parseAsString.withDefault(ReferralReportTab.Stage1),
  );
  const [, setUserType] = useQueryState('userType', parseAsString.withDefault(ReferralUserType.Customer));
  const [, setPage] = useQueryState('page', parseAsInteger.withDefault(0));

  const { affiliateReferralReports, isLoading, pagination } = useAffiliateReferralReport();
  const { referralAnalytics, isLoading: isReferralAnalyticsLoading } = useReferralAnalytics(userType);

  const onTabChange = (tab: string): void => {
    void Promise.all([setActiveTabProp(tab), setPage(0)]);
  };

  const tabItems = [
    {
      label: t('dashboard.referralReports.tabs.stage1'),
      value: ReferralReportTab.Stage1,
      content: <CustomerReferralStage1Table />,
    },
    {
      label: t('dashboard.referralReports.tabs.stage2'),
      value: ReferralReportTab.Stage2,
      content: <CustomerReferralStage2Table />,
    },
  ];

  return (
    <>
      <div className="relative">
        <div className="absolute right-0 -top-[66px]">
          <Select
            name="userType"
            value={userType}
            triggerLabel={t('common.form.label.userType')}
            labelInTrigger
            valueClassName="text-base"
            options={[
              { label: t('common.form.label.customer'), value: ReferralUserType.Customer },
              { label: t('common.form.label.affiliate'), value: ReferralUserType.Affiliate },
            ]}
            onValueChange={(value) => {
              void Promise.all([setUserType(value), setPage(0)]);
            }}
            itemClassName="cursor-pointer"
            triggerClassName="w-auto min-w-[190px] cursor-pointer justify-center gap-2 rounded-sm border border-primary bg-transparent px-4 py-3 text-sm font-bold shadow-xs [&_svg]:size-4"
          />
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <Can rule={{ allOf: [PERMS.customerPoint.referral.analytics] }}>
          <ReferralOverviewCards overview={referralAnalytics} isLoading={isReferralAnalyticsLoading} />
        </Can>
        {userType === ReferralUserType.Customer ? (
          <Can rule={{ allOf: [PERMS.referral.customerReport.read] }}>
            <Tabs
              className="rounded-[8px] bg-muted shadow"
              listClassName="bg-muted"
              triggerClassName="bg-muted rounded-t-[8px] px-6 py-3"
              defaultActive={activeTab ?? activeTabProp}
              value={activeTab ?? activeTabProp}
              items={tabItems}
              onChange={onTabChange}
            />
          </Can>
        ) : (
          <Can rule={{ allOf: [PERMS.affiliate.referral.report.view] }}>
            <AffiliateReferralReportTable
              data={affiliateReferralReports}
              isLoading={isLoading}
              pagination={pagination}
            />
          </Can>
        )}
      </div>
    </>
  );
}
